import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import { Button, Modal } from '../../../../Components/uielements';
import {
  ModalButtonWrapper,
  ModalGap,
  OutlineButton,
} from '../../../../services/settings/appStyle/global.style';
import { IntlMessages } from '../../../../helper/langHelper';
import { createCheckList, updateCheckList } from '../../../../services/bioplus-services/checkList';
import {
  FormSection,
  InputGroup,
  Input,
  QuestionsContainer,
  QuestionCard,
  QuestionHeader,
  DeleteButton,
  QuestionRow,
  FlexInput,
  Select,
  CheckboxWrapper,
  Checkbox,
  OptionsContainer,
  OptionRow,
  OptionInput,
  AddOptionButton,
  RemoveOptionButton,
  AddQuestionButton,
  ErrorText,
} from '../checkList.style';
import { ModalWrapper } from '../../EmailTemplate/emailTemplate.style';
import { ModalLabel } from '../../UserV2/user.style';
import { useUserContext } from '../../../../hooks/UserContext';
import { useCompany } from '../../../../hooks/CompanyContext';

const QUESTION_TYPES = {
  STRING: 'STRING',
  NUMBER: 'NUMBER',
  OPTION: 'OPTION',
  TEXTAREA: 'TEXTAREA',
  MEDIA: 'MEDIA',
  ACKNOWLEDGEMENT: 'ACKNOWLEDGEMENT',
};

const FILE_TYPES = {
  IMAGE: 'IMAGE',
  PDF: 'PDF',
};

const createEmptyQuestion = () => ({
  question: '',
  type: QUESTION_TYPES.STRING,
  required: false,
  options: [],
  fileType: FILE_TYPES.IMAGE,
});

const createOrUpdateCheckList = async (actionEditOrCreate, values) => {
  if (actionEditOrCreate === 'edit') {
    return await updateCheckList(values);
  }
  return await createCheckList(values);
};

const CreateCheckListModal = ({ onClose, data, actionEditOrCreate, checkListId }) => {
  const queryClient = useQueryClient();
  const [title, setTitle] = useState('');
  const [questions, setQuestions] = useState([createEmptyQuestion()]);
  // const [acknowledgement, setAcknowledgement] = useState({
  //   content: '',
  //   required: false,
  // });
  const [errors, setErrors] = useState({});

  const companyContext = useCompany();
  const companyFilter = companyContext?.company?._id;
  const userContext = useUserContext();

  // Initialize form data for edit mode
  useEffect(() => {
    if (data && actionEditOrCreate === 'edit') {
      setTitle(data.title || '');

      if (data.questionnaire && data.questionnaire.length > 0) {
        const normalQuestions = [];
        // let ack = { content: '', required: false };

        data.questionnaire.forEach((q) => {
          // if (q.type === QUESTION_TYPES.ACKNOWLEDGEMENT) {
          //   // Set acknowledgement state
          //   ack = {
          //     content: q.question || '',
          //     required: q.required || false,
          //   };
          // } else {
          normalQuestions.push({
            ...q,
            options: q.options || [],
            fileType: q.fileType || FILE_TYPES.IMAGE,
          });
          // }
        });

        setQuestions(normalQuestions.length > 0 ? normalQuestions : [createEmptyQuestion()]);
        // setAcknowledgement(ack);
      }
    }
  }, [data, actionEditOrCreate]);

  const mutation = useMutation((values) => createOrUpdateCheckList(actionEditOrCreate, values), {
    onSuccess: async () => {
      await queryClient.invalidateQueries('checkLists');
      onClose();
    },
    onError: (error) => {
      alert(`Error: ${error?.response?.data?.message}`);
    },
  });

  const validateForm = () => {
    const newErrors = {};

    if (!title.trim()) {
      newErrors.title = 'Title is required';
    }

    questions.forEach((q, index) => {
      if (!q.question.trim()) {
        newErrors[`question_${index}`] = 'Question text is required';
      }

      if (q.type === QUESTION_TYPES.OPTION) {
        const validOptions = q.options.filter((opt) => opt.trim());
        if (validOptions.length < 2) {
          newErrors[`options_${index}`] = 'At least 2 options are required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const payload = {
      title: title.trim(),
      userType: companyFilter
        ? 'OWNER'
        : userContext?.company?.role === 'EMPLOYEE'
        ? 'OWNER'
        : userContext?.company?.role || 'ADMIN',
      listType: 'CHECKLIST',
      companyId: companyFilter ?? userContext?.company?.companyId?._id,
      ...(actionEditOrCreate === 'edit' && { questionnaireId: checkListId }),
      questions: questions.map((q) => {
        const baseQuestion = {
          question: q.question.trim(),
          type: q.type,
          required: q.required,
        };

        if (q.type === QUESTION_TYPES.OPTION) {
          baseQuestion.options = q.options.filter((opt) => opt.trim());
        }

        if (q.type === QUESTION_TYPES.MEDIA) {
          baseQuestion.fileType = q.fileType;
        }

        return baseQuestion;
      }),
    };

    // Add acknowledgement if content exists
    // if (acknowledgement.content.trim()) {
    //   payload.questions.push({
    //     question: acknowledgement.content.trim(),
    //     type: QUESTION_TYPES.ACKNOWLEDGEMENT,
    //     required: acknowledgement.required,
    //   });
    // }

    mutation.mutate(payload);
  };

  const addQuestion = () => {
    setQuestions([...questions, createEmptyQuestion()]);
  };

  const removeQuestion = (index) => {
    if (questions.length > 1) {
      setQuestions(questions.filter((_, i) => i !== index));
      // Clear errors for this question
      const newErrors = { ...errors };
      delete newErrors[`question_${index}`];
      delete newErrors[`options_${index}`];
      setErrors(newErrors);
    }
  };

  const updateQuestion = (index, field, value) => {
    const updated = [...questions];
    updated[index] = { ...updated[index], [field]: value };

    // If type changes to OPTION, ensure options array exists
    if (field === 'type' && value === QUESTION_TYPES.OPTION) {
      if (!updated[index].options || updated[index].options.length === 0) {
        updated[index].options = ['', ''];
      }
    }

    // If type changes to MEDIA, ensure fileType exists
    if (field === 'type' && value === QUESTION_TYPES.MEDIA) {
      if (!updated[index].fileType) {
        updated[index].fileType = FILE_TYPES.IMAGE;
      }
    }

    setQuestions(updated);

    // Clear related errors
    if (field === 'question') {
      const newErrors = { ...errors };
      delete newErrors[`question_${index}`];
      setErrors(newErrors);
    }
  };

  const addOption = (questionIndex) => {
    const updated = [...questions];
    updated[questionIndex].options = [...updated[questionIndex].options, ''];
    setQuestions(updated);
  };

  const removeOption = (questionIndex, optionIndex) => {
    const updated = [...questions];
    updated[questionIndex].options = updated[questionIndex].options.filter(
      (_, i) => i !== optionIndex
    );
    setQuestions(updated);

    // Clear option errors if we have enough options now
    if (updated[questionIndex].options.filter((opt) => opt.trim()).length >= 2) {
      const newErrors = { ...errors };
      delete newErrors[`options_${questionIndex}`];
      setErrors(newErrors);
    }
  };

  const updateOption = (questionIndex, optionIndex, value) => {
    const updated = [...questions];
    updated[questionIndex].options[optionIndex] = value;
    setQuestions(updated);

    // Clear option errors if we have enough valid options
    const validOptions = updated[questionIndex].options.filter((opt) => opt.trim());
    if (validOptions.length >= 2) {
      const newErrors = { ...errors };
      delete newErrors[`options_${questionIndex}`];
      setErrors(newErrors);
    }
  };

  const isEditMode = actionEditOrCreate === 'edit';

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      isUppercase={true}
      title={
        isEditMode ? (
          <IntlMessages id="modal.title.edit.checklist" />
        ) : (
          <IntlMessages id="modal.title.checklist" />
        )
      }
    >
      <form onSubmit={handleSubmit}>
        <ModalWrapper>
          <FormSection>
            <InputGroup>
              <ModalLabel>Title *</ModalLabel>
              <Input
                type="text"
                value={title}
                onChange={(e) => {
                  setTitle(e.target.value);
                  if (errors.title) {
                    setErrors({ ...errors, title: undefined });
                  }
                }}
                placeholder="Enter checklist title"
              />
              {errors.title && <ErrorText>{errors.title}</ErrorText>}
            </InputGroup>

            <div>
              <ModalLabel style={{ marginBottom: '12px' }}>Questions</ModalLabel>
              <QuestionsContainer>
                {questions.map((question, qIndex) => (
                  <QuestionCard key={qIndex}>
                    <QuestionHeader>
                      <ModalLabel>Question {qIndex + 1}</ModalLabel>
                      {questions.length > 1 && (
                        <DeleteButton type="button" onClick={() => removeQuestion(qIndex)}>
                          Delete
                        </DeleteButton>
                      )}
                    </QuestionHeader>

                    <QuestionRow>
                      {question.type === QUESTION_TYPES.TEXTAREA ? (
                        <textarea
                          rows={3}
                          value={question.question}
                          onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                          placeholder="Enter your question"
                          style={{
                            flex: 1,
                            padding: '8px 12px',
                            border: '1px solid #d9d9d9',
                            borderRadius: '4px',
                            fontSize: '14px',
                            fontFamily: 'inherit',
                            resize: 'vertical',
                            backgroundColor: 'transparent',
                            color: 'white',
                          }}
                        />
                      ) : (
                        <FlexInput
                          type="text"
                          value={question.question}
                          onChange={(e) => updateQuestion(qIndex, 'question', e.target.value)}
                          placeholder="Enter your question"
                        />
                      )}
                      <Select
                        value={question.type}
                        onChange={(e) => updateQuestion(qIndex, 'type', e.target.value)}
                      >
                        <option value={QUESTION_TYPES.STRING}>Text</option>
                        <option value={QUESTION_TYPES.NUMBER}>Number</option>
                        <option value={QUESTION_TYPES.OPTION}>Multiple Choice</option>
                        <option value={QUESTION_TYPES.TEXTAREA}>Multi line text</option>
                        <option value={QUESTION_TYPES.MEDIA}>Media Upload</option>
                      </Select>
                    </QuestionRow>

                    {errors[`question_${qIndex}`] && (
                      <ErrorText>{errors[`question_${qIndex}`]}</ErrorText>
                    )}

                    {question.type === QUESTION_TYPES.MEDIA && (
                      <InputGroup style={{ marginTop: '12px' }}>
                        <ModalLabel>
                          File Type <span>(Maximum file size allowed: 2 MB)</span>
                        </ModalLabel>
                        <Select
                          value={question.fileType}
                          onChange={(e) => updateQuestion(qIndex, 'fileType', e.target.value)}
                        >
                          <option value={FILE_TYPES.IMAGE}>Image</option>
                          <option value={FILE_TYPES.PDF}>PDF</option>
                        </Select>
                      </InputGroup>
                    )}

                    <CheckboxWrapper>
                      <Checkbox
                        type="checkbox"
                        id={`required_${qIndex}`}
                        checked={question.required}
                        onChange={(e) => updateQuestion(qIndex, 'required', e.target.checked)}
                      />
                      <ModalLabel htmlFor={`required_${qIndex}`}>Required</ModalLabel>
                    </CheckboxWrapper>

                    {question.type === QUESTION_TYPES.OPTION && (
                      <OptionsContainer>
                        {question.options.map((option, oIndex) => (
                          <OptionRow key={oIndex}>
                            <OptionInput
                              type="text"
                              value={option}
                              onChange={(e) => updateOption(qIndex, oIndex, e.target.value)}
                              placeholder={`Option ${oIndex + 1}`}
                            />
                            {question.options.length > 2 && (
                              <RemoveOptionButton
                                type="button"
                                onClick={() => removeOption(qIndex, oIndex)}
                              >
                                Remove
                              </RemoveOptionButton>
                            )}
                          </OptionRow>
                        ))}
                        <div>
                          <AddOptionButton type="button" onClick={() => addOption(qIndex)}>
                            + Add Option
                          </AddOptionButton>
                        </div>
                        {errors[`options_${qIndex}`] && (
                          <ErrorText>{errors[`options_${qIndex}`]}</ErrorText>
                        )}
                      </OptionsContainer>
                    )}
                  </QuestionCard>
                ))}
              </QuestionsContainer>

              <AddQuestionButton type="button" onClick={addQuestion}>
                + Add Question
              </AddQuestionButton>
            </div>

            {/* Acknowledgement Section - Always visible at bottom */}
            {/* <div>
              <ModalLabel style={{ marginBottom: '12px' }}>Acknowledgement</ModalLabel>
              <QuestionCard>
                <InputGroup>
                  <ModalLabel>Acknowledgement Text</ModalLabel>
                  <textarea
                    rows={3}
                    value={acknowledgement.content}
                    onChange={(e) =>
                      setAcknowledgement({ ...acknowledgement, content: e.target.value })
                    }
                    placeholder="Enter acknowledgement text that users must agree to..."
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #d9d9d9',
                      borderRadius: '4px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'vertical',
                      backgroundColor: 'transparent',
                      color: 'white',
                    }}
                  />
                </InputGroup>

                <CheckboxWrapper style={{ marginTop: '12px' }}>
                  <Checkbox
                    type="checkbox"
                    id="acknowledgement_required"
                    checked={acknowledgement.required}
                    onChange={(e) =>
                      setAcknowledgement({ ...acknowledgement, required: e.target.checked })
                    }
                  />
                  <ModalLabel htmlFor="acknowledgement_required">
                    Require signature/agreement
                  </ModalLabel>
                </CheckboxWrapper>
              </QuestionCard>
            </div> */}
          </FormSection>

          <ModalGap />

          <ModalButtonWrapper style={{ marginTop: '0px' }}>
            <OutlineButton type="button" onClick={onClose}>
              <IntlMessages id="modal.button.cancel" />
            </OutlineButton>
            <Button varient="normal" type="submit" disabled={mutation?.isLoading}>
              {isEditMode ? (
                <IntlMessages id="button.text.update" />
              ) : (
                <IntlMessages id="button.text.create" />
              )}
              {mutation?.isLoading && '...'}
            </Button>
          </ModalButtonWrapper>
        </ModalWrapper>
      </form>
    </Modal>
  );
};

export default CreateCheckListModal;
